import { observer } from 'mobx-react-lite'
import type { CSSProperties, FC } from 'react'
import { useCallback, useMemo, useState } from 'react'
import type { Placement } from '@floating-ui/react'
import type { Stores } from '../../../../util/store'
import { useModels } from '../../../../stores/models-store'
import { Modal } from '../../../../components/modal'
import type { SelectableListItem } from '../../../../components/modals'
import { SelectableList } from '../../../../components/modals'
import fuzzysort from 'fuzzysort'
import { ModelsModalFuzzySearch } from './models-modal-fuzzy-search'
import { ManufacturersModal } from './manufacturers-modal'
import { MdChevronLeft } from 'react-icons/md'
import styles from './models-modal.module.scss'
import { useManufacturerById } from '../../../../stores/manufacturers-store'

export interface ModelsModalProps {
    anchor?: HTMLElement
    placement?: Placement
    stores: Stores
    onModelIdsChange(ids: string[]): void
    style?: CSSProperties
    containerStyle?: CSSProperties
    onBack?(): void
    selectedIds?: Set<string>
    fuzzyMatch?: string
    manufacturerId?: string
}

export enum ModelsModalStep {
    Manufacturers,
    Models,
}

export const ModelsModal: FC<ModelsModalProps> = observer(
    ({
        anchor,
        placement,
        stores,
        onModelIdsChange,
        style,
        containerStyle,
        onBack,
        selectedIds,
        fuzzyMatch,
        manufacturerId: initialManufacturerId,
    }) => {
        const models = useModels(stores)
        const [manufacturerId, setManufacturerId] = useState(
            initialManufacturerId,
        )
        const [step, setStep] = useState(
            manufacturerId
                ? ModelsModalStep.Models
                : ModelsModalStep.Manufacturers,
        )

        const [currentlySelectedIds, setCurrentlySelectedIds] = useState(
            () => selectedIds ?? new Set<string>(),
        )

        const select = useCallback(
            (modelId: string) => {
                const clone = new Set(Array.from(currentlySelectedIds))

                if (clone.has(modelId)) {
                    clone.delete(modelId)
                } else {
                    clone.add(modelId)
                }

                setCurrentlySelectedIds(clone)
            },
            [currentlySelectedIds],
        )

        const items = useMemo(() => {
            const filteredModels = manufacturerId
                ? models.filter(
                      (model) =>
                          model.product_manufacturer_id === manufacturerId,
                  )
                : models

            return filteredModels.map<SelectableListItem>((model) => {
                const manufacturer =
                    stores.manufacturers.manufacturersById[
                        model.product_manufacturer_id
                    ]
                return {
                    id: model.id,
                    name: `${manufacturer?.name} ${model.name}`,
                }
            })
        }, [models, manufacturerId, stores.manufacturers.manufacturersById])

        const results = useMemo(() => {
            if (!fuzzyMatch) {
                return
            }

            return fuzzysort.go(fuzzyMatch, items, {
                key: 'name',
                threshold: 0.5,
                limit: 5,
            })
        }, [fuzzyMatch, items])

        const doClose = useCallback(() => {
            onModelIdsChange(Array.from(currentlySelectedIds))
        }, [onModelIdsChange, currentlySelectedIds])

        const manufacturer = useManufacturerById(manufacturerId, stores)
        const manufacturerName = manufacturer?.name

        if (step === ModelsModalStep.Manufacturers) {
            return (
                <ManufacturersModal
                    anchor={anchor}
                    placement={placement}
                    stores={stores}
                    onClose={doClose}
                    style={style}
                    containerStyle={containerStyle}
                    onBack={onBack}
                    selectedId={manufacturerId}
                    onSelect={(id) => {
                        setManufacturerId(id)
                        setStep(ModelsModalStep.Models)
                    }}
                />
            )
        }

        return (
            <Modal
                placement={placement}
                anchor={anchor}
                onClose={doClose}
                style={style}
                containerStyle={containerStyle}
                onBack={onBack}>
                <div className={styles.headerRow}>
                    <MdChevronLeft
                        size={24}
                        className={styles.backIcon}
                        onClick={() => {
                            setStep(ModelsModalStep.Manufacturers)
                        }}
                    />

                    {manufacturerName && (
                        <div
                            className={styles.manufacturerName}
                            title={manufacturerName}>
                            {manufacturerName}
                        </div>
                    )}
                </div>

                <ModelsModalFuzzySearch
                    results={results}
                    selectedIds={currentlySelectedIds}
                    onSelect={select}
                />

                <SelectableList
                    items={items}
                    selectedIds={currentlySelectedIds}
                    onSelect={(item) => select(item.id)}
                    isLoading={
                        stores.models.isFetching || stores.models.isUpdating
                    }
                    placeholder="Search model"
                />
            </Modal>
        )
    },
)
